import { z } from "zod";
import { claimCodeSchema } from "./ranking";
import { frozenSlideSchema } from "./deck";
import { lobbySchema } from "./lobby";
import { themeSchema } from "./theme";
import { qnaQuestionSchema } from "./qna";
import { joinedNameSchema, leaderboardEntrySchema, stageSchema } from "./realtime";

/**
 * Presenter → server, over authenticated HTTP.
 *
 * Separate from the participant contracts because the trust model is different in
 * kind, not degree: a participant holds a six-character code typed off a screen, while
 * a host holds an authenticated organisation session. Keeping the two sets of schemas
 * apart means no host field can ever be reachable from the unauthenticated join path
 * by an accidental import.
 */

const uuid = z.string().uuid();

/**
 * What a session does, decided once when it is created.
 *
 * These are read by the participant PWA through the public projection, so every one of
 * them is a deliberate choice about what strangers on the internet can see or do.
 */
export const sessionSettingsSchema = z
  .object({
    /** Collect no display names at all. Overrides requireName. */
    anonymous: z.boolean().default(false),
    /** Refuse to admit a participant who has not given a name. */
    requireName: z.boolean().default(false),
    qnaEnabled: z.boolean().default(false),
    /** Hold questions for the presenter to approve before anyone else sees them. */
    qnaModeration: z.boolean().default(true),
    showLeaderboard: z.boolean().default(true),
  })
  .strict();

export type SessionSettings = z.infer<typeof sessionSettingsSchema>;

/**
 * How a session runs.
 *
 * - `live`: the presenter drives it. Everyone sees the same slide at the same moment,
 *   rounds open and close on the presenter's word, and the room watches results arrive.
 * - `self_paced`: shared as a code or link and filled in by each person in their own
 *   time, until it closes. Each question's clock starts when that person is shown it,
 *   so timed questions still score by speed (Rule 2), and only the presenter sees the
 *   standings. See ADR 0008.
 */
export const SESSION_MODES = ["live", "self_paced"] as const;
export const sessionModeSchema = z.enum(SESSION_MODES);
export type SessionMode = z.infer<typeof sessionModeSchema>;

/** The longest a self-paced session may stay open: a term's homework, not forever. */
export const SELF_PACED_MAX_DAYS = 90;

export const createSessionSchema = z
  .object({
    deckId: uuid,
    settings: sessionSettingsSchema.partial().default({}),
    mode: sessionModeSchema.default("live"),
    /**
     * Self-paced only: when it stops taking answers. Absent, it stays open until the
     * presenter closes it. Must be in the future and within SELF_PACED_MAX_DAYS — the
     * server checks both against its own clock.
     */
    closesAt: z.string().datetime({ offset: true }).nullable().default(null),
  })
  .strict()
  .refine((r) => r.mode === "self_paced" || r.closesAt === null, {
    path: ["closesAt"],
    message: "only a self-paced session closes by itself",
  });

export type CreateSessionRequest = z.infer<typeof createSessionSchema>;

/**
 * Open a round on one slide.
 *
 * By slide id, not by index. The presenter can jump backwards, re-run a slide, or skip
 * one, and an index would silently address a different slide after any of those — the
 * snapshot is frozen, so the id is stable for the whole session and says exactly what
 * was meant.
 */
export const openRoundSchema = z.object({ slideId: uuid }).strict();

export type OpenRoundRequest = z.infer<typeof openRoundSchema>;

/**
 * Move the room on one step — the presenter's "Next".
 *
 * `fromStep` is the step the presenter was looking at when they pressed it. If the room
 * has already moved past it (a double click, or "Next" on a second screen) the server
 * refuses with 409 instead of skipping a slide nobody saw.
 */
export const advanceSchema = z.object({ fromStep: z.number().int().nonnegative() }).strict();

/** The lobby's QR code filling the screen, or back in its place (`lobby.qr`). */
export const lobbyQrSchema = z.object({ large: z.boolean() }).strict();
export type LobbyQrRequest = z.infer<typeof lobbyQrSchema>;

export type AdvanceRequest = z.infer<typeof advanceSchema>;

export const advanceResultSchema = z
  .object({
    stage: stageSchema,
    /** True when that step was past the last slide: the session is over. */
    ended: z.boolean(),
  })
  .strict();

export type AdvanceResult = z.infer<typeof advanceResultSchema>;

/**
 * The presenter's view of a session. Unlike `PublicSession` this carries the join code
 * and the deck, because the host is authenticated and owns both.
 */
export const hostSessionSchema = z
  .object({
    sessionId: uuid,
    orgId: z.string(),
    status: z.enum(["lobby", "live", "ended"]),
    // Defaulted: a presenter screen may hold a session read before modes existed.
    mode: sessionModeSchema.default("live"),
    /** Self-paced: when it stops taking answers, if it does by itself. */
    closesAt: z.string().datetime().nullable().default(null),
    joinCode: z.string(),
    settings: sessionSettingsSchema,
    /** From the frozen snapshot: the deck as it is being run, not as it is now. */
    deckTitle: z.string(),
    slideCount: z.number().int().nonnegative(),
    participantCount: z.number().int().nonnegative(),
    /** The plan's audience cap: joins beyond it are refused. */
    participantLimit: z.number().int().positive(),
    startedAt: z.string().datetime().nullable(),
    endedAt: z.string().datetime().nullable(),
  })
  .strict();

export type HostSession = z.infer<typeof hostSessionSchema>;

/** What opening a round tells the presenter, so their own UI need not wait for the broadcast. */
export const openRoundResultSchema = z
  .object({
    roundId: uuid,
    slideId: uuid,
    openedAt: z.string().datetime(),
    timeLimitMs: z.number().int().nonnegative(),
  })
  .strict();

export type OpenRoundResult = z.infer<typeof openRoundResultSchema>;

/**
 * The latest round of a session, as the presenter needs it to render from scratch.
 *
 * `counts` has the same keys as `tally.updated`: option ids for choice slides, the
 * lower-cased word for word clouds, the value as a string for scales.
 */
export const liveRoundStateSchema = z
  .object({
    roundId: uuid,
    slideId: uuid,
    openedAt: z.string().datetime(),
    timeLimitMs: z.number().int().nonnegative(),
    closed: z.boolean(),
    revealed: z.boolean(),
    counts: z.record(z.string(), z.number().int().nonnegative()),
    responseCount: z.number().int().nonnegative(),
    /** Free-text slides: the most recent answers, newest first. Empty otherwise. */
    texts: z.array(z.string()),
  })
  .strict();

export type LiveRoundState = z.infer<typeof liveRoundStateSchema>;

/**
 * Everything the presenter screen needs on load — and on reload, which is the case
 * that matters: a presenter who refreshes mid-round must come back to the round they
 * left, with its tally, not to a blank deck with a round still silently open.
 *
 * After this, the screen is kept current by realtime events. The slides include
 * `isCorrect`: the host owns the deck. The UI is what keeps correct answers off the
 * projected screen until they are revealed.
 */
export const hostLiveStateSchema = z
  .object({
    session: hostSessionSchema,
    slides: z.array(frozenSlideSchema),
    /** The look the deck was frozen with — the default for a deck that had none. */
    theme: themeSchema,
    lobby: lobbySchema,
    round: liveRoundStateSchema.nullable(),
    stage: stageSchema,
    leaderboard: z.array(leaderboardEntrySchema),
    /** The latest names to join, newest first — the lobby's list (`participants.updated`). */
    joined: z.array(joinedNameSchema).default([]),
    /** Whether the lobby's QR code fills the screen (`lobby.qr`). */
    qrLarge: z.boolean().default(false),
    /** Every question, all statuses, newest first. Empty when Q&A is off. */
    questions: z.array(qnaQuestionSchema),
    /**
     * The server's clock at the time of this read. Round deadlines are on the
     * server's clock, and a projector laptop's clock can be minutes out; the screen
     * uses this to correct its countdown rather than trusting its own time.
     */
    serverTime: z.string().datetime(),
  })
  .strict();

export type HostLiveState = z.infer<typeof hostLiveStateSchema>;

/**
 * One person in a session, as the presenter's list of people shows them: who to remove
 * when a name on the leaderboard shouldn't be there.
 */
export const sessionParticipantSchema = z
  .object({
    participantId: uuid,
    displayName: z.string().nullable(),
    score: z.number().int(),
    joinedAt: z.string().datetime(),
    /** What their phone shows, to prove a prize is theirs (ranking.ts). Never on the stage. */
    claimCode: claimCodeSchema.nullable(),
  })
  .strict();

export type SessionParticipant = z.infer<typeof sessionParticipantSchema>;

export const sessionParticipantsSchema = z.array(sessionParticipantSchema);
